import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import ApiService from "../../services/apiService";
import PumpImg from "../IrootImages/pump.png";
import ValveImg from "../IrootImages/valve_v.png";
import SensorImg from "../IrootImages/sensor_v.png";

import { LucideCross, LucidePanelLeftClose, LucideRefreshCcw, LucideSidebarClose, LucideTrendingUpDown, TrendingUpDown, X } from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY = "#1e563d";
const PUMP_BLUE = "#185FA5";
const PUMP_BLUE_BG = "#E6F1FB";
const PAGE_SIZE = 10;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FarmApiRow {
  farmId: number;
  farmName: string;
  farmLat: string;
  farmLng: string;
  totalPump: number;
  totalValve: number;
  totalSensor: number;
  onPump: number;
  onValve: number;
  onSensor: number;
  alarm: number;
  cropsName_Id: number;
  sowDateTime: string;
  landSize: number;
  englishName: string;
  englishType: string;
  mandiReference: string;
  cropImageUrl: string;
}

interface Crop {
  id: number;
  name: string;
  type: string;
  sowDateTime: string;
  mandiRef: string;
  imageUrl: string;
}

interface Farm {
  farmId: number;
  farmName: string;
  farmLat: string;
  farmLng: string;
  landSize: number;
  crops: Crop[];
  totalValve: number;
  totalPump: number;
  totalSensor: number;
  onValve: number;
  onPump: number;
  onSensor: number;
}

interface TimelineEvent {
  id: number | string;
  farmId: number | string;
  dateTime: string;
  alarmEvent: number;
  operationType: string;
  userName?: string | null;
  autoManual?: string | null;
  description?: string | null;
  schedulerName?: string | null;
  remark?: string | null;
  instrumentName?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function groupFarms(rows: FarmApiRow[]): Farm[] {
  const map = new Map<number, Farm>();
  rows.forEach((row) => {
    if (!map.has(row.farmId)) {
      map.set(row.farmId, {
        farmId: row.farmId,
        farmName: row.farmName,
        farmLat: row.farmLat,
        farmLng: row.farmLng,
        landSize: row.landSize,
        crops: [],
        totalValve: row.totalValve,
        totalPump: row.totalPump,
        totalSensor: row.totalSensor,
        onValve: row.onValve,
        onPump: row.onPump,
        onSensor: row.onSensor,
      });
    }
    map.get(row.farmId)!.crops.push({
      id: row.cropsName_Id,
      name: row.englishName,
      type: row.englishType,
      sowDateTime: row.sowDateTime,
      mandiRef: row.mandiReference,
      imageUrl: row.cropImageUrl,
    });
  });
  return Array.from(map.values());
}

function formatDate(dt: string): string {
  return new Date(dt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmt(
  dt: string | null | undefined,
  mode: "full" | "time" | "date" = "full",
): string {
  if (!dt) return "—";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "—";
  if (mode === "time")
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  if (mode === "date")
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function groupByDate(evs: TimelineEvent[]) {
  const m = new Map<string, TimelineEvent[]>();
  evs.forEach((ev) => {
    const k = fmt(ev.dateTime, "date");
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(ev);
  });
  return Array.from(m.entries()).map(([date, items]) => ({ date, items }));
}

function isIrrigating(farm: Farm): boolean {
  return farm.onPump > 0 || farm.onValve > 0;
}

// ─── Timeline event config ────────────────────────────────────────────────────

function evCfg(
  operationType: string,
  alarmEvent: number,
): {
  dot: string;
  bg: string;
  border: string;
  category: string;
} {
  const op = operationType.toLowerCase().trim();
  if (alarmEvent === 1) {
    if (op.includes("phase"))
      return {
        dot: "#A32D2D",
        bg: "#fef2f2",
        border: "#fca5a5",
        category: "alarm",
      };
    if (op.includes("over"))
      return {
        dot: "#854F0B",
        bg: "#faeeda",
        border: "#fde68a",
        category: "alarm",
      };
    if (op.includes("under"))
      return {
        dot: "#854F0B",
        bg: "#faeeda",
        border: "#fde68a",
        category: "alarm",
      };
    if (op.includes("temp"))
      return {
        dot: "#854F0B",
        bg: "#faeeda",
        border: "#fde68a",
        category: "alarm",
      };
    if (op.includes("battery"))
      return {
        dot: "#A32D2D",
        bg: "#fef2f2",
        border: "#fca5a5",
        category: "alarm",
      };
    return {
      dot: "#A32D2D",
      bg: "#fef2f2",
      border: "#fca5a5",
      category: "alarm",
    };
  }
  if (op.includes("schedule")) {
    return alarmEvent === 1
      ? {
          dot: "#A32D2D",
          bg: "#fef2f2",
          border: "#fca5a5",
          category: "schedule",
        } // Schedule Interrupt
      : {
          dot: "#1e563d",
          bg: "#f0fdf4",
          border: "#bbf7d0",
          category: "schedule",
        }; // Schedule Starting
  }

  if (op.includes("pump on"))
    return { dot: PRIMARY, bg: "#e8f2ed", border: "#c6ddd3", category: "pump" };
  if (op.includes("pump off"))
    return {
      dot: "#475569",
      bg: "#f1f5f9",
      border: "#e2e8f0",
      category: "pump",
    };
  if (op.includes("valve open"))
    return {
      dot: PRIMARY,
      bg: "#e8f2ed",
      border: "#c6ddd3",
      category: "valve",
    };
  if (op.includes("valve close"))
    return {
      dot: "#475569",
      bg: "#f1f5f9",
      border: "#e2e8f0",
      category: "valve",
    };
  if (op.includes("phase ok"))
    return {
      dot: "#3B6D11",
      bg: "#e8f2ed",
      border: "#c6ddd3",
      category: "alarm",
    };
  if (op.includes("auto"))
    return {
      dot: PUMP_BLUE,
      bg: PUMP_BLUE_BG,
      border: "#bfdbfe",
      category: "mode",
    };
  if (op.includes("manual"))
    return {
      dot: "#854F0B",
      bg: "#faeeda",
      border: "#fde68a",
      category: "mode",
    };
  if (op.includes("remote"))
    return {
      dot: "#0F6E56",
      bg: "#E1F5EE",
      border: "#9FE1CB",
      category: "mode",
    };
  if (op.includes("local"))
    return {
      dot: "#64748b",
      bg: "#f1f5f9",
      border: "#e2e8f0",
      category: "mode",
    };
  return {
    dot: "#94a3b8",
    bg: "#f8fafc",
    border: "#e2e8f0",
    category: "other",
  };
}

const TL_FILTERS = [
  { key: "all", label: "All", match: (_ev: TimelineEvent) => true },
  {
    key: "pump",
    label: "Pump",
    match: (ev: TimelineEvent) =>
      evCfg(ev.operationType, ev.alarmEvent).category === "pump",
  },
  {
    key: "valve",
    label: "Valve",
    match: (ev: TimelineEvent) =>
      evCfg(ev.operationType, ev.alarmEvent).category === "valve",
  },
  {
    key: "alarm",
    label: "Alarms",
    match: (ev: TimelineEvent) =>
      ev.alarmEvent === 1 ||
      evCfg(ev.operationType, ev.alarmEvent).category === "alarm",
  },
  {
    key: "mode",
    label: "Mode",
    match: (ev: TimelineEvent) =>
      evCfg(ev.operationType, ev.alarmEvent).category === "mode",
  },
];

// ─── Timeline sub-components ──────────────────────────────────────────────────

const DateDiv: React.FC<{ date: string }> = ({ date }) => {
  const today = fmt(new Date().toISOString(), "date");
  const yest = fmt(new Date(Date.now() - 86_400_000).toISOString(), "date");
  const label = date === today ? "Today" : date === yest ? "Yesterday" : date;
  return (
    <div className="ft-date-divider">
      <div className="ft-date-divider-line" />
      <span className="ft-date-divider-label">{label}</span>
      <div className="ft-date-divider-line" />
    </div>
  );
};

const TLCard: React.FC<{ ev: TimelineEvent; isLast: boolean }> = ({ ev, isLast }) => {
  const cfg = evCfg(ev.operationType, ev.alarmEvent);
  const isAlarm = ev.alarmEvent === 1;

  const chipMode =
    ev.autoManual === "Auto Mode"
      ? "ft-tl-chip--auto"
      : ev.autoManual
        ? "ft-tl-chip--manual"
        : "";

  // 2. hasChips me include karo
  const hasChips = ev.userName || ev.autoManual || ev.schedulerName || ev.instrumentName;

  return (
    <div className="ft-tl-row">
      {/* Spine */}
      <div className="ft-tl-spine">
        <div
          className="ft-tl-dot"
          style={{ background: cfg.bg, border: `1.5px solid ${cfg.dot}` }}
        >
          <span className="ft-tl-dot-letter" style={{ color: cfg.dot }}>
            {isAlarm ? "A" : "E"}
          </span>
        </div>
        {!isLast && <div className="ft-tl-line" />}
      </div>

      {/* Card */}
      <div className={`ft-tl-card ${isLast ? "ft-tl-card--last" : ""}`}>
        {/* Top row: tag + time */}
        <div className="ft-tl-card-head">
          <span
            className="ft-tl-tag"
            style={{ background: cfg.bg, color: cfg.dot, border: `1px solid ${cfg.border}` }}
          >
            {ev.operationType}
          </span>
          <span className="ft-tl-time">{fmt(ev.dateTime, "time")}</span>
        </div>

        {/* Chips: userName, schedulerName, autoManual */}
        {hasChips && (
          <div className="ft-tl-chips">
            {ev.userName && (
              <span className="ft-tl-chip ft-tl-chip--user">
                <i className="ti ti-user" aria-hidden="true" />
                {ev.userName}
              </span>
            )}
            {ev.schedulerName && (
              <span className="ft-tl-chip ft-tl-chip--scheduler">
                <i className="ti ti-calendar-time" aria-hidden="true" />
                {ev.schedulerName}
              </span>
            )}
            {ev.autoManual && (
              <span className={`ft-tl-chip ${chipMode}`}>
                <i className="ti ti-settings-2" aria-hidden="true" />
                {ev.autoManual}
              </span>
            )}

            {ev.instrumentName && (
              <span className="ft-tl-chip ft-tl-chip--instrument">
                <i className="ti ti-device-analytics" aria-hidden="true" />
                {ev.instrumentName}
              </span>
            )}
          </div>
        )}

        {/* Remark — OUTSIDE chips div, always shown if present */}
        {ev.remark && (
          <div className="ft-tl-remark">
            {ev.remark}
          </div>
        )}
      </div>
    </div>
  );
};
// ─── Farm Timeline Panel ──────────────────────────────────────────────────────

const FarmTimeline: React.FC<{ farm: Farm; onClose: () => void }> = ({
  farm,
  onClose,
}) => {
  const [evs, setEvs] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const load = useCallback(
    async (page: number, append: boolean) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;

      if (page === 1) {
        setLoading(true);
        setError(null);
        setEvs([]);
      } else setLoadingMore(true);

      const now = new Date();
      const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      const isoIST =
        `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}T` +
        `${String(ist.getHours()).padStart(2, "0")}:${String(ist.getMinutes()).padStart(2, "0")}:${String(ist.getSeconds()).padStart(2, "0")}`;

      try {
        const res = await ApiService.axiosApi(`/timelinemaster/get-timeline`, {
          method: "GET",
          params: {
            farmId: farm.farmId,
            dateTime: isoIST,
            pageNumber: page,
            pageSize: PAGE_SIZE,
          },
        });

        if (res.success) {
          const raw: any[] = Array.isArray(res.data.data) ? res.data.data : [];
          const mapped: TimelineEvent[] = raw.map((item: any) => ({
            id: item.id,
            farmId: item.farmId ?? farm.farmId,
            dateTime: item.dateTime,
            alarmEvent: item.alarm_Event ?? 0,
            operationType: item.operationType ?? "Unknown",
            userName: item.userName ?? null,
            autoManual: item.auto_Manual ?? null,
            description: item.remark ?? null,
            schedulerName: item.schedulerName ?? null,
            remark: item.remark ?? null,
              instrumentName: item.instrumentName ?? null, // ✅ add this
          }));
          setHasMore(raw.length === PAGE_SIZE);
          pageRef.current = page;
          setEvs((prev) => (append ? [...prev, ...mapped] : mapped));
        } else {
          setError("Failed to load timeline.");
        }
      } catch {
        setError("Network error.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
        fetchingRef.current = false;
      }
    },
    [farm.farmId],
  );

  // Initial load
  useEffect(() => {
    pageRef.current = 1;
    setHasMore(true);
    load(1, false);
  }, [farm.farmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scroller = scrollRef.current;
    if (!sentinel || !scroller) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) {
          load(pageRef.current + 1, true);
        }
      },
      { root: scroller, rootMargin: "0px", threshold: 0.1 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasMore, load, evs]);

  const filtered = evs.filter((ev) =>
    TL_FILTERS.find((f) => f.key === filter)!.match(ev),
  );
  const grouped = groupByDate(filtered);

  return (
    <div className="ft-overlay" onClick={onClose}>
      <div className="ft-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ft-header">
          <div className="ft-header-info">
            <span className="ft-farm-name">{farm.farmName}</span>
            <span className="ft-sub-label">Farm timeline</span>
          </div>
          <div className="ft-header-actions">
            <button
              className="ft-icon-btn"
              onClick={() => {
                setRefreshing(true);
                pageRef.current = 1;
                setHasMore(true);
                load(1, false);
              }}
              disabled={refreshing}
            >
              < LucideRefreshCcw
                className={`ti ti-refresh ${refreshing ? "ft-spinning" : ""}`}
                aria-hidden="true"
              />
            </button>
            <button
              className="ft-icon-btn ft-icon-btn--close"
              onClick={onClose}
            >
              < X className="ti ti-x" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        {/* <div className="ft-filters">
          {TL_FILTERS.map(f => {
            const cnt    = f.key === "all" ? evs.length : evs.filter(ev => f.match(ev)).length;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`ft-filter-btn ${active ? "ft-filter-btn--active" : ""}`}
              >
                {f.label}
                <span className={`ft-filter-count ${active ? "ft-filter-count--active" : "ft-filter-count--inactive"}`}>
                  {cnt}
                </span>
              </button>
            );
          })}
        </div> */}

        {/* Scrollable body */}
        <div ref={scrollRef} className="ft-body">
          {loading ? (
            <div className="ft-state">
              <i
                className="ti ti-loader-2 ft-spinning"
                aria-hidden="true"
                style={{ fontSize: 18, color: PRIMARY }}
              />
              <span className="ft-state-text">Loading…</span>
            </div>
          ) : error ? (
            <div className="ft-state">
              <i
                className="ti ti-alert-circle"
                aria-hidden="true"
                style={{ fontSize: 16, color: "#A32D2D" }}
              />
              <span className="ft-state-text--err">{error}</span>
              <button className="ft-retry-btn" onClick={() => load(1, false)}>
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="ft-state ft-state--col">
              <i
                className="ti ti-clock-hour-3"
                aria-hidden="true"
                style={{ fontSize: 28, color: "#e2e8f0" }}
              />
              <span className="ft-state-text--empty">No events found</span>
            </div>
          ) : (
            <>
              {grouped.map(({ date, items }) => (
                <div key={date}>
                  <DateDiv date={date} />
                  {items.map((ev, idx) => (
                    <TLCard
                      key={ev.id}
                      ev={ev}
                      isLast={idx === items.length - 1}
                    />
                  ))}
                </div>
              ))}

              {/* Sentinel for infinite scroll */}
              {hasMore && (
                <div ref={sentinelRef} className="ft-sentinel">
                  <i
                    className="ti ti-loader-2"
                    aria-hidden="true"
                    style={{
                      fontSize: 16,
                      color: "#94a3b8",
                      animation: "ftspin .9s linear infinite",
                      opacity: loadingMore ? 1 : 0,
                    }}
                  />
                </div>
              )}

              {!hasMore && evs.length > 0 && (
                <div className="ft-all-loaded">
                  — All {evs.length} events loaded —
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── InstrumentStatRow ────────────────────────────────────────────────────────

const InstrumentStatRow: React.FC<{
  label: string;
  icon: string;
  on: number;
  total: number;
  onColor: string;
}> = ({ label, icon, on, total, onColor }) => (
  <div className="fs-instr-stat-item">
    <div className="fs-instr-stat-icon">
      <img src={icon} alt={label} />
    </div>
    <span className="fs-instr-stat-frac">
      <span className="on-count" style={{ color: onColor }}>
        {on}
      </span>
      <span className="off-count">/{total}</span>
    </span>
    <span className="fs-instr-stat-label">{label}</span>
  </div>
);

// ─── CropRow ──────────────────────────────────────────────────────────────────

const CropRow: React.FC<{ crop: Crop }> = ({ crop }) => (
  <div className="fs-crop-row">
    {crop.imageUrl ? (
      <img
        src={crop.imageUrl}
        alt={crop.name}
        className="fs-crop-img"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    ) : (
      <div className="fs-crop-img-fallback">
        <i
          className="ti ti-plant-2"
          aria-hidden="true"
          style={{ fontSize: 13, color: PRIMARY }}
        />
      </div>
    )}
    <div>
      <div className="fs-crop-name">{crop.name}</div>
      <div className="fs-crop-meta">
        {crop.type} · Sown {formatDate(crop.sowDateTime)}
      </div>
    </div>
  </div>
);

// ─── FarmCard ─────────────────────────────────────────────────────────────────

const FarmCard: React.FC<{
  farm: Farm;
  onClick?: (f: Farm) => void;
  onTimelineClick: (f: Farm) => void;
}> = ({ farm, onClick, onTimelineClick }) => {
  const irrigating = isIrrigating(farm);

  return (
    <Card className="fs-farm-card" onClick={() => onClick?.(farm)}>
      {/* Header */}
      <div className="fs-farm-header">
        <span className="fs-farm-header-name">{farm.farmName}</span>

        <div className="fs-header-right">
          <span
            className={`fs-irrig-badge ${irrigating ? "fs-irrig-badge--on" : "fs-irrig-badge--off"}`}
          >
            <span
              className={`fs-irrig-dot ${irrigating ? "fs-irrig-dot--on" : "fs-irrig-dot--off"}`}
            />
            {irrigating ? "Irrigation on" : "Irrigation off"}
          </span>

          <button
            className="fs-tl-icon-btn"
            title="View timeline"
            onClick={(e) => {
              e.stopPropagation();
              onTimelineClick(farm);
            }}
          >
            <TrendingUpDown />
          </button>
        </div>
      </div>

      <CardBody className="fs-farm-body">
        <div className="fs-meta-row">
          <i className="ti ti-ruler fs-meta-icon" aria-hidden="true" />
          <span>{farm.landSize} acres</span>
        </div>
        <div className="fs-meta-row">
          <i className="ti ti-map-pin fs-meta-icon" aria-hidden="true" />
          <span style={{ fontSize: 11, color: "#6c757d" }}>
            {parseFloat(farm.farmLat).toFixed(4)},{" "}
            {parseFloat(farm.farmLng).toFixed(4)}
          </span>
        </div>

        <div className="fs-instr-stats-box">
          <InstrumentStatRow
            label="Valves"
            icon={ValveImg}
            on={farm.onValve}
            total={farm.totalValve}
            onColor={PRIMARY}
          />
          <InstrumentStatRow
            label="Pumps"
            icon={PumpImg}
            on={farm.onPump}
            total={farm.totalPump}
            onColor="#185FA5"
          />
          <InstrumentStatRow
            label="Sensors"
            icon={SensorImg}
            on={farm.onSensor}
            total={farm.totalSensor}
            onColor="#0F6E56"
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {farm.crops.map((crop) => (
            <CropRow key={crop.id} crop={crop} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

// ─── AddFarmCard ──────────────────────────────────────────────────────────────

const AddFarmCard: React.FC<{ onAdd?: () => void }> = ({ onAdd }) => (
  <Card className="fs-add-card" onClick={onAdd}>
    <CardBody className="fs-add-body">
      <i className="ti ti-plus fs-add-icon" aria-hidden="true" />
      <span className="fs-add-label">Add farm</span>
    </CardBody>
  </Card>
);

// ─── FarmsSection ─────────────────────────────────────────────────────────────

interface FarmsSectionProps {
  onFarmClick?: (farm: Farm) => void;
  onAddFarm?: () => void;
  onViewAll?: () => void;
  userId?: string | number;
}

const FarmsSection: React.FC<FarmsSectionProps> = ({
  onFarmClick,
  onAddFarm,
  onViewAll,
  userId,
}) => {
  const [farmInfo, setFarmInfo] = useState<FarmApiRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [timelineFarm, setTimelineFarm] = useState<Farm | null>(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const getFarmInfo = async () => {
      try {
        const res = await ApiService.axiosApi(
          `/farmsmaster/getfarminstrumentcropdetailsbyuserid/${userId}`,
          { method: "GET" },
        );
        if (res.success) setFarmInfo(res.data);
        else console.warn("Farm Section:", res.data);
      } catch (e) {
        console.error("Farm Section", e);
      } finally {
        setLoading(false);
      }
    };
    getFarmInfo();
  }, [userId]);

  const farms = useMemo(() => groupFarms(farmInfo), [farmInfo]);

  if (loading)
    return (
      <p style={{ fontSize: 12, color: "#6c757d", padding: "8px 0" }}>
        Loading farms...
      </p>
    );

  return (
    <div className="fs-wrapper">
      <div className="fs-section-header">
        <p className="fs-section-label">FARMS</p>
        <button className="fs-view-all-btn" onClick={onViewAll}>
          View all →
        </button>
      </div>

      {farms.length === 0 ? (
        <p style={{ fontSize: 12, color: "#6c757d" }}>No farms found.</p>
      ) : (
        <Row style={{ margin: "0 -5px" }}>
          {farms.map((farm) => (
            <Col key={farm.farmId} xs={12} sm={6} lg={4} className="fs-col">
              <FarmCard
                farm={farm}
                onClick={onFarmClick}
                onTimelineClick={setTimelineFarm}
              />
            </Col>
          ))}
          <Col xs={12} sm={6} lg={4} className="fs-col">
            <AddFarmCard onAdd={onAddFarm} />
          </Col>
        </Row>
      )}

      {/* Sliding timeline panel */}
      {timelineFarm && (
        <FarmTimeline
          farm={timelineFarm}
          onClose={() => setTimelineFarm(null)}
        />
      )}
    </div>
  );
};

export default FarmsSection;
